import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { continueWatchingData, FetchContinueWatchWidget, removeUserContinueWatching, Widget, WidgetContinueCarousel } from '../../../apiService';
import { useUser } from '../../../CacheProvider';
import Carousel from '../../../Carousel';
import { useViewersOwnFlags } from '../../../utils/useViewerAccess';

const ContinueWatchingList = dynamic(() => import('../ContinueWatching/ContinueWatchingList'));

const toContents = (items: WidgetContinueCarousel[]) => items.map(({ video_content }) => video_content);

const ContinueWatchingWidget = ({ widget }: { widget: Widget<'continue_watching'> }) => {
  const {
    configuration: { items, navigation_configuration },
    display_title,
  } = widget;
  const more = navigation_configuration?.api_url || navigation_configuration?.url;
  const { authorized, loaded } = useUser();
  const ownFlags = useViewersOwnFlags();
  const [continueData, setContinueData] = useState<continueWatchingData[]>(() => toContents(items));
  const [showArrow, setShowArrow] = useState<boolean>(true);
  useEffect(() => {
    setContinueData(toContents(items));
  }, [items]);
  // Requested anonymously, the widget comes without the viewer's list, so it's fetched on its own.
  useEffect(() => {
    if (!authorized || ownFlags !== false) return;
    let cancelled = false;
    FetchContinueWatchWidget({ handledByCaller: true })
      .then(({ data }) => { if (!cancelled && data?.[0]) setContinueData(toContents(data[0].configuration.items)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [authorized, ownFlags]);
  const handleDeleteContent = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    e.stopPropagation();
    await removeUserContinueWatching({ id });
    const listData: continueWatchingData[] = [...continueData];
    const index = listData.findIndex((item) => item.id === id);
    listData.splice(index, 1);
    setContinueData(listData);
  };
  useEffect(() => {
    const offsetWidth = Number(window.innerWidth);
    if (offsetWidth > 1280 && continueData.length <= 8) {
      setShowArrow(false);
    } else if (960 > offsetWidth && offsetWidth <= 1280 && continueData.length <= 6) {
      setShowArrow(false);
    } else if (offsetWidth <= 960) {
      setShowArrow(false);
    } else {
      setShowArrow(true);
    }
  }, [continueData]);

  // It's the viewer's alone, so it isn't shown once they turn out to be logged out (e.g. an expired SSO session).
  if (loaded && !authorized) return null;
  return <>
    {continueData && continueData.length > 0 && <Carousel m={0} title={display_title} more={more} arrows={showArrow}>
      {<ContinueWatchingList items={continueData} deleteContent={handleDeleteContent} />}
    </Carousel>}
  </>;
};

export default ContinueWatchingWidget;
